/**
 * Deduplicates order-history API rows that share the same order id by merging
 * field values so a single canonical row reflects partial fills and cancellation.
 */

export function getOrderHistoryRowKey(order) {
    const raw = order?.id ?? order?.order_id ?? order?.orderId;
    if (raw !== undefined && raw !== null && `${raw}` !== '') {
        return `${raw}`;
    }
    return null;
}

/** First positive numeric quantity across common API/socket field names. */
export function resolveOrderQuantity(row) {
    if (!row || typeof row !== 'object') return 0;

    const candidates = [
        row.quantity,
        row.qty,
        row.order_quantity,
        row.orderQuantity,
        row.original_quantity,
        row.originalQuantity,
    ];

    for (const candidate of candidates) {
        const parsed = Number(candidate);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }

    return 0;
}

/** Human-readable wallet type for order history rows (API: METAMASK, BITGO, etc.). */
export function formatOrderWalletTypeLabel(row) {
    const raw = String(row?.walletType ?? row?.wallet_type ?? '').trim();
    if (!raw) return '--';

    const key = raw.toLowerCase().replace(/[\s-]+/g, '_');
    if (key.includes('metamask')) return 'MetaMask';
    if (key.includes('bitgo')) return 'BitGo';
    if (key.includes('trust')) return 'Trust Wallet';
    if (key.includes('walletconnect')) return 'WalletConnect';
    if (key.includes('coinbase')) return 'Coinbase Wallet';
    if (key.includes('non_custodial') || key === 'nc') return 'Non-Custodial';
    if (key.includes('custodial')) return 'Custodial';

    return raw
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function normalizeOrderHistoryRow(row) {
    if (!row || typeof row !== 'object') return null;

    const quantity = resolveOrderQuantity(row);
    if (!(quantity > 0)) return null;

    const filled = Number(row.filled_amount ?? row.filledAmount ?? 0);

    return {
        ...row,
        quantity,
        filled_amount: Number.isFinite(filled) && filled >= 0 ? filled : 0,
    };
}

/** Map USER_TRADE / CANCEL_ORDER socket payloads to an order-history row shape. */
export function buildOrderHistoryRowFromSocket(parsedOrder, eventStatus) {
    if (!parsedOrder || typeof parsedOrder !== 'object') return null;

    const id = parsedOrder.id ?? parsedOrder.order_id ?? parsedOrder.orderId;
    if (id == null || `${id}`.trim() === '') return null;

    const quantity = resolveOrderQuantity(parsedOrder);
    if (!(quantity > 0)) return null;

    const filled = Number(
        parsedOrder.filled_amount ?? parsedOrder.filledAmount ?? 0,
    );

    let status = parsedOrder.status;
    if (eventStatus === 2) {
        status = 2;
    } else if (status == null && eventStatus != null) {
        status = eventStatus;
    }

    return normalizeOrderHistoryRow({
        id,
        price: parsedOrder.price ?? parsedOrder.executed_price ?? parsedOrder.executedPrice,
        quantity,
        side: parsedOrder.side,
        type: parsedOrder.type ?? parsedOrder.orderType,
        filled_amount: Number.isFinite(filled) ? filled : 0,
        executed_price:
            parsedOrder.executed_price ??
            parsedOrder.executedPrice ??
            parsedOrder.price,
        status,
        advanceOrderType: parsedOrder.advanceOrderType,
        wallet_type: parsedOrder.wallet_type ?? parsedOrder.walletType,
        walletType: parsedOrder.walletType ?? parsedOrder.wallet_type,
    });
}

/** Reject partial socket rows that would create 0-qty ghost entries in history. */
export function isEligibleOrderHistoryRow(row) {
    if (!row || getOrderHistoryRowKey(row) == null) return false;
    return resolveOrderQuantity(row) > 0;
}

/**
 * @param {Array<Record<string, unknown>>} rows
 * @returns {Array<Record<string, unknown>>}
 */
export function mergeOrderHistoryByOrderId(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return [];
    }

    /** @type {Map<string, Record<string, unknown>[]>} */
    const groups = new Map();
    /** @type {string[]} */
    const orderKeys = [];

    rows.forEach((row, idx) => {
        const normalized = normalizeOrderHistoryRow(row);
        if (!normalized || !isEligibleOrderHistoryRow(normalized)) return;

        const key = getOrderHistoryRowKey(normalized);
        const groupKey =
            key != null ? key : `__row:${idx}:${normalized?.price ?? ''}:${normalized?.quantity ?? ''}:${normalized?.status ?? ''}`;

        if (!groups.has(groupKey)) {
            groups.set(groupKey, []);
            orderKeys.push(groupKey);
        }
        groups.get(groupKey).push(normalized);
    });

    return orderKeys.map((k) => mergeOrderHistoryGroup(groups.get(k)));
}

/**
 * @param {Record<string, unknown>[]} items
 */
function mergeOrderHistoryGroup(items) {
    if (items.length === 1) {
        return { ...items[0] };
    }

    /** Later rows override earlier snapshots (API order), then reconcile amounts & status. */
    /** @type {Record<string, unknown>} */
    const mergedRow = {};
    let maxFilled = 0;
    let maxQuantity = 0;
    let hasCancelled = false;
    let hasPartialStatus = false;
    let hasCompleted = false;
    let hasPending = false;
    let hasSpeedbump = false;

    for (const it of items) {
        Object.assign(mergedRow, it);
        maxQuantity = Math.max(maxQuantity, resolveOrderQuantity(it));
        const status = Number(it.status);
        if (status === 2) hasCancelled = true;
        if (status === 4) hasPartialStatus = true;
        if (status === 3) hasCompleted = true;
        if (status === 1) hasPending = true;
        if (status === 6) hasSpeedbump = true;
        maxFilled = Math.max(maxFilled, Number(it.filled_amount) || 0);
    }

    const existingFilled = Number(mergedRow.filled_amount) || 0;
    mergedRow.filled_amount = Math.max(existingFilled, maxFilled);
    if (maxQuantity > 0) {
        mergedRow.quantity = maxQuantity;
    }

    if (hasCancelled) {
        mergedRow.status = 2;
    } else if (hasCompleted) {
        mergedRow.status = 3;
    } else if (hasPartialStatus) {
        mergedRow.status = 4;
    } else if (hasSpeedbump) {
        mergedRow.status = 6;
    } else if (hasPending) {
        mergedRow.status = 1;
    }

    return mergedRow;
}
